import { tv } from 'tailwind-variants'
import { IHubFolder, ISidebarResource } from 'types'

// The page header's plain parts, apart from the component so that anything can
// import them without pulling in the project hook (which, through its own
// imports, reaches the document toolbar and would import it in a loop).

/**
 * The header's buttons: the table toolbar's borderless 28px squares with a
 * hover fill (`icon`), and one labelled action (`action`) such as Edit. The
 * Button's focus-visible outline marks keyboard focus.
 */
export const pageHeaderButton = tv({
  slots: {
    icon: 'h-7 w-7 p-0 rounded border-transparent bg-transparent text-body hover:bg-surface-sunken [&>svg]:w-4 [&>svg]:h-4',
    // In the manner of Notion's Share: no border, the icon and word held tight
    // so they read as one action, and a hover fill. Its icon is 14px: a
    // corner-to-corner glyph like the pencil drew bigger than every other icon
    // at the row's 16px.
    action:
      'h-7 px-2 gap-1 rounded border-transparent bg-transparent text-[13px] font-medium text-body hover:bg-surface-sunken [&>svg]:w-3.5 [&>svg]:h-3.5'
  },
  variants: {
    // The table toolbar's colour for a toggle that's on.
    active: {
      true: { icon: 'bg-light-teal hover:bg-light-teal' }
    },
    // A labelled action that's switched on (Done, while editing) is tinted
    // blue, so the way back out is easy to find.
    pressed: {
      true: { action: 'bg-really-light-blue text-blue hover:bg-light-blue hover:text-blue' }
    }
  }
})

/**
 * The folder a resource sits in, for the breadcrumb: the sidebar's own folder
 * for it, so the crumb follows a move in the sidebar straight away. There's no
 * crumb at the hub's top level, or for a resource the sidebar doesn't list.
 */
export const breadcrumbFolder = (
  folders: IHubFolder[],
  resources: ISidebarResource[],
  resource: Pick<ISidebarResource, 'kind' | 'publicId'> | undefined
): string | null => {
  if (!resource) return null
  const folderId = resources.find((r) => r.kind === resource.kind && r.publicId === resource.publicId)?.folderId
  if (!folderId) return null
  return folders.find((f) => f.publicId === folderId)?.name ?? null
}

/**
 * The header's action area. Groups of related actions sit tight inside a
 * group with a clear gap between groups, so the row reads as a few tools
 * rather than a strip of equals. The margin puts the same gap before the
 * first group.
 */
export const PAGE_HEADER_ACTIONS = 'flex items-center shrink-0 gap-4 ml-2'
export const PAGE_HEADER_GROUP = 'inline-flex items-center shrink-0 gap-0.5'
